import React, { useState } from 'react';
import { ACTIVE_GREEN, ACTIVE_GREEN_BG } from '../../../constants/statusColors';
import {
  Paper,
  Box,
  Typography,
  Button,
  List,
  ListItem,
  Chip,
  IconButton,
  Divider,
  Avatar,
  Tabs,
  Tab,
} from '@mui/material';
import {
  AddOutlined as AddIcon,
  BusinessCenterOutlined as ExecutiveIcon,
  EditOutlined as EditIcon,
  DeleteOutlined as DeleteIcon,
  EmailOutlined as EmailIcon,
  PhoneOutlined as PhoneIcon,
  VerifiedOutlined as SignatoryIcon,
} from '@mui/icons-material';
import AddOfficerModal from '../../../components/companies/AddOfficerModal';
import DeleteConfirmDialog from '../../../components/common/DeleteConfirmDialog';
import { csAdminApi } from '../../../services/api';

const OfficersTab = ({ companyId, companyName, officers = [], onRefresh }) => {
  const [selectedOfficer, setSelectedOfficer] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [officerToDelete, setOfficerToDelete] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0 = Current, 1 = Past

  // Filter current and past officers
  const currentOfficers = officers.filter(o => o.status?.toLowerCase() === 'active');
  const pastOfficers = officers.filter(o => o.status?.toLowerCase() !== 'active');

  const displayedOfficers = activeTab === 0 ? currentOfficers : pastOfficers;

  // Get position icon and color
  const getPositionColor = (position) => {
    const pos = position?.toLowerCase() || '';
    if (pos.includes('ceo') || pos.includes('chief executive')) {
      return { bg: '#E3F2FD', color: '#1976D2', icon: '👔' };
    } else if (pos.includes('cfo') || pos.includes('financial')) {
      return { bg: '#E8F5E9', color: '#388E3C', icon: '💰' };
    } else if (pos.includes('coo') || pos.includes('operating')) {
      return { bg: '#FFF3E0', color: '#F57C00', icon: '⚙️' };
    } else if (pos.includes('secretary')) {
      return { bg: '#F3E5F5', color: '#7B1FA2', icon: '📋' };
    } else if (pos.includes('legal')) {
      return { bg: '#FFEBEE', color: '#D32F2F', icon: '⚖️' };
    } else if (pos.includes('compliance')) {
      return { bg: '#E0F2F1', color: '#00796B', icon: '✓' };
    } else {
      return { bg: '#F5F5F5', color: '#757575', icon: '👤' };
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-GB');
  };

  const calculateTenure = (startDate, endDate) => {
    if (!startDate) return 'N/A';
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();
    
    const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    
    if (years > 0 && remainingMonths > 0) {
      return `${years}y ${remainingMonths}m`;
    } else if (years > 0) {
      return `${years} year${years > 1 ? 's' : ''}`;
    } else if (remainingMonths > 0) {
      return `${remainingMonths} month${remainingMonths > 1 ? 's' : ''}`;
    } else {
      return 'Less than 1 month';
    }
  };

  const handleAddOfficer = () => {
    setShowAddModal(true);
    setSelectedOfficer(null);
  };

  const handleEditOfficer = (officer) => {
    setSelectedOfficer(officer);
    setShowAddModal(true);
  };

  const handleDeleteOfficer = (officer) => {
    setOfficerToDelete(officer);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      if (officerToDelete?.id) {
        await csAdminApi.deleteCompanyOfficer(companyId, officerToDelete.id);
      }
    } catch (err) {
      console.error('Delete officer:', err);
    }
    setShowDeleteDialog(false);
    setOfficerToDelete(null);
    if (onRefresh) onRefresh();
  };

  const handleAddOrUpdateOfficer = async (officerData) => {
    try {
      {
        const payload = {
          name: officerData.name,
          position: officerData.position,
          email: officerData.email,
          phone: officerData.phone,
          appointmentDate: officerData.appointmentDate,
          isAuthorizedSignatory: officerData.isAuthorizedSignatory,
          status: officerData.status || 'ACTIVE',
        };
        if (selectedOfficer?.id) {
          await csAdminApi.updateCompanyOfficer(companyId, selectedOfficer.id, payload);
        } else {
          await csAdminApi.createCompanyOfficer(companyId, payload);
        }
      }
      setShowAddModal(false);
      setSelectedOfficer(null);
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Save officer:', err);
      throw err;
    }
  };

  const officerModals = (
    <>
      <AddOfficerModal
        open={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setSelectedOfficer(null);
        }}
        onAdd={handleAddOrUpdateOfficer}
        companyId={companyId}
        companyName={companyName}
        existingOfficers={officers}
        editOfficer={selectedOfficer}
      />

      <DeleteConfirmDialog
        open={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setOfficerToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Remove Officer"
        message={`Are you sure you want to remove "${officerToDelete?.name}" from the officers list?`}
      />
    </>
  );

  if (!officers || officers.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Company Officers</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddOfficer}
            size="small"
          >
            Add Officer
          </Button>
        </Box>

        <Box textAlign="center" py={6}>
          <ExecutiveIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="body1" color="textSecondary" gutterBottom>
            No company officers recorded
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Add key executives and authorized personnel
          </Typography>
        </Box>

        {officerModals}
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6">Company Officers</Typography>
          <Typography variant="caption" color="textSecondary">
            {currentOfficers.length} active, {pastOfficers.length} past
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddOfficer}
          size="small"
        >
          Add Officer
        </Button>
      </Box>

      {/* Tabs for Current/Past */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label={`Current (${currentOfficers.length})`} />
          <Tab label={`Past (${pastOfficers.length})`} />
        </Tabs>
      </Box>

      {/* Officers List */}
      {displayedOfficers.length === 0 ? (
        <Box textAlign="center" py={4}>
          <Typography variant="body2" color="textSecondary">
            No {activeTab === 0 ? 'current' : 'past'} officers
          </Typography>
        </Box>
      ) : (
        <List sx={{ p: 0 }}>
          {displayedOfficers.map((officer, index) => {
            const positionStyle = getPositionColor(officer.position);
            
            return (
              <React.Fragment key={officer.id}>
                <ListItem
                  sx={{
                    px: 0,
                    py: 2.5,
                    '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.02)' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, width: '100%' }}>
                    {/* Avatar/Icon */}
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: positionStyle.bg,
                        color: positionStyle.color,
                        fontSize: '1.5rem',
                        fontWeight: 600,
                      }}
                    >
                      {officer.name?.charAt(0) || positionStyle.icon}
                    </Avatar>

                    {/* Officer Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                          {officer.name}
                        </Typography>
                        {officer.isAuthorizedSignatory && (
                          <Chip
                            icon={<SignatoryIcon sx={{ fontSize: '0.9rem' }} />}
                            label="Authorized Signatory"
                            size="small"
                            sx={{
                              bgcolor: ACTIVE_GREEN_BG,
            color: ACTIVE_GREEN,
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              height: 20,
                            }}
                          />
                        )}
                        <Chip
                          label={officer.status || 'Active'}
                          size="small"
                          sx={{
                            bgcolor: officer.status?.toLowerCase() === 'active' ? '#E3F2FD' : '#F5F5F5',
                            color: officer.status?.toLowerCase() === 'active' ? '#1976D2' : '#757575',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 20,
                            textTransform: 'uppercase',
                          }}
                        />
                      </Box>
                      
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 1, fontWeight: 500 }}>
                        {officer.position}
                      </Typography>

                      {/* Contact Details */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
                        {officer.email && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="caption" color="textSecondary">
                              {officer.email}
                            </Typography>
                          </Box>
                        )}
                        {officer.phone && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="caption" color="textSecondary">
                              {officer.phone}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {/* Tenure Details */}
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Typography variant="caption" color="textSecondary">
                          <strong>Appointed:</strong> {formatDate(officer.appointmentDate)}
                        </Typography>
                        {officer.termEndDate && (
                          <Typography variant="caption" color="textSecondary">
                            • <strong>Term Ended:</strong> {formatDate(officer.termEndDate)}
                          </Typography>
                        )}
                        <Typography variant="caption" color="textSecondary">
                          • <strong>Tenure:</strong> {calculateTenure(officer.appointmentDate, officer.termEndDate)}
                        </Typography>
                        {officer.authorityLevel && (
                          <Chip
                            label={`Authority: ${officer.authorityLevel}`}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              bgcolor: '#FFF3E0',
                              color: '#E65100',
                            }}
                          />
                        )}
                      </Box>
                    </Box>

                    {/* Actions */}
                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditOfficer(officer)}
                        title="Edit"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteOfficer(officer)}
                        title="Remove"
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </ListItem>
                {index < displayedOfficers.length - 1 && <Divider />}
              </React.Fragment>
            );
          })}
        </List>
      )}

      {/* Add/Edit Officer Modal */}
      {officerModals}
    </Paper>
  );
};

export default OfficersTab;
